import React, { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import { Link } from "react-router-dom";
import { getRequest } from "../../utils/httpRequest";

interface Course {
  id: number;
  name: string;
  course_path: string;
  tile_description?: string | null;
  preview_mini_description?: string | null;
  preview_headshot_image_url?: string | null;
  preview_headshot_alt_image_url?: string | null;
  lessons_count: number;
  skill_area_name?: string | null;
  sample: boolean;
}

const RORY_PAGE = "/thought-leaders/rory-sutherland";

// Every live course Rory teaches or appears in, drawn the same way as on his
// own page, so the two never disagree.
const RoryCourses = () => {
  const [courses, setCourses] = useState<Course[]>([]);

  useEffect(() => {
    (getRequest("/api/thought-leaders/rory-sutherland.json") as Promise<AxiosResponse<{ courses: Course[] }>>)
      .then((r) => setCourses(r.data.courses || []))
      .catch(() => setCourses([]));
  }, []);

  if (courses.length === 0) return null;
  const [featured, ...others] = courses;
  const image = (c: Course) => c.preview_headshot_image_url || c.preview_headshot_alt_image_url;
  const meta = (c: Course) =>
    [c.lessons_count ? `${c.lessons_count} lessons` : null, c.skill_area_name].filter(Boolean).join(" \u00B7 ");

  return (
    <section className="tlp-section tlp-courses rq-courses" id="learn-from-rory">
      <div className="tlp-section__head">
        <h2>Learn from Rory</h2>
        <p>
          {courses.length} courses Rory teaches or appears in. If you only take one, take the first.{" "}
          <Link to={RORY_PAGE}>More about Rory</Link>.
        </p>
      </div>

      <Link className="tlp-feature" to={featured.course_path}>
        <div className="tlp-feature__img">{image(featured) && <img src={image(featured) as string} alt="" loading="lazy" />}</div>
        <div className="tlp-feature__body">
          <span className="tlp-kicker">Start here</span>
          <h3>{featured.name}</h3>
          {(featured.tile_description || featured.preview_mini_description) && (
            <p>{featured.tile_description || featured.preview_mini_description}</p>
          )}
          <div className="tlp-meta">
            {featured.lessons_count > 0 && <span>{featured.lessons_count} lessons</span>}
            <span>CPD certified</span>
            {featured.skill_area_name && <span>{featured.skill_area_name}</span>}
          </div>
          <span className="tlp-btn">Explore this course</span>
        </div>
      </Link>

      {others.length > 0 && (
        <div className="tlp-course-grid">
          {others.map((course) => (
            <Link className="tlp-course" key={course.id} to={course.course_path}>
              <div className="tlp-course__img">{image(course) && <img src={image(course) as string} alt="" loading="lazy" />}</div>
              <div className="tlp-course__body">
                <h4>{course.name}</h4>
                {meta(course) && <span>{meta(course)}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default RoryCourses;
